/**
 * @fileoverview Minimal plug-and-play drawer custom element (right side only)
 *
 * @example
 * // Basic usage with internal toggle
 * <drawer-element id="cart-drawer">
 *   <drawer-toggle>
 *     <button>Open</button>
 *   </drawer-toggle>
 *   <drawer-content>
 *     <button class="close">X</button>
 *     <p>Content here</p>
 *   </drawer-content>
 * </drawer-element>
 *
 * @example
 * // External toggle using drawer-id
 * <drawer-toggle drawer-id="my-drawer">
 *   <button>Open Drawer</button>
 * </drawer-toggle>
 *
 * @example
 * // Programmatic control via window events
 * window.dispatchEvent(new Event('open-drawer-my-drawer'));
 * window.dispatchEvent(new Event('close-drawer-my-drawer'));
 *
 * @note The element owns dialog-level focus. Opening moves focus into the panel and traps
 *   Tab inside it; closing hands focus back to whatever opened the drawer and drops the
 *   role="dialog" / aria-modal="true" pair, so a closed drawer stops advertising itself as
 *   an open modal. Content is slotted, so the focusable nodes live in the light DOM under
 *   <drawer-content>, not in the shadow root that renders the panel.
 */

const DRAWER_STYLES = `
  :host { display: contents; }

  .backdrop {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    justify-content: flex-end;
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.3s ease, visibility 0.3s ease;
  }

  .backdrop.open {
    visibility: visible;
    opacity: 1;
  }

  .drawer-content {
    background: var(--drawer-bg, #fff);
    height: 100dvh;
    width: var(--drawer-width, auto);
    max-width: 100%;
    transform: translateX(100%);
    transition: transform 0.3s ease;
    overflow-y: auto;
  }

  .backdrop.open .drawer-content {
    transform: translateX(0);
  }

  @media (prefers-reduced-motion: reduce) {
    .backdrop, .drawer-content {
      transition: none;
    }
  }
`;

/**
 * Elements that can hold focus. `:not([tabindex="-1"])` keeps programmatic-only targets —
 * such as the panel itself — out of the Tab cycle while still allowing .focus() on them.
 */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button",
  "input",
  "select",
  "textarea",
  "details > summary",
  "iframe",
  "[contenteditable]",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

class DrawerElement extends HTMLElement {
  /** @type {HTMLElement|null} */
  #backdrop = null;
  /** Where focus was when the drawer opened, so it can be handed back on close */
  #returnFocusTo = null;
  #boundOpen = null;
  #boundClose = null;
  #boundKeydown = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot.innerHTML = `
      <style>${DRAWER_STYLES}</style>
      <slot name="toggle"></slot>
      <div class="backdrop">
        <div class="drawer-content">
          <slot name="content"></slot>
        </div>
      </div>
    `;

    // Bind methods for proper cleanup
    this.#boundOpen = () => this.open();
    this.#boundClose = () => this.close();
    this.#boundKeydown = (e) => {
      if (e.key === "Escape") {
        this.close();
        return;
      }
      if (e.key === "Tab") this.#trapTab(e);
    };
  }

  connectedCallback() {
    this.#backdrop = this.shadowRoot.querySelector(".backdrop");

    // Move to body for proper stacking
    if (!this.parentElement?.isSameNode(document.body)) {
      document.body.appendChild(this);
    }

    // Assign slots
    this.querySelector("drawer-toggle")?.setAttribute("slot", "toggle");
    this.querySelector("drawer-content")?.setAttribute("slot", "content");

    // Internal toggle click
    this.querySelector("drawer-toggle")?.addEventListener(
      "click",
      this.#boundOpen,
    );

    // Backdrop click to close
    this.#backdrop?.addEventListener("click", (e) => {
      if (e.target === this.#backdrop) this.close();
    });

    // Close button inside content
    this.querySelector(".close")?.addEventListener("click", this.#boundClose);

    // Window events for external control
    const id = this.id;
    if (id) {
      window.addEventListener(`open-drawer-${id}`, this.#boundOpen);
      window.addEventListener(`close-drawer-${id}`, this.#boundClose);
    }
  }

  disconnectedCallback() {
    const id = this.id;
    if (id) {
      window.removeEventListener(`open-drawer-${id}`, this.#boundOpen);
      window.removeEventListener(`close-drawer-${id}`, this.#boundClose);
    }
    document.removeEventListener("keydown", this.#boundKeydown);
  }

  /** Opens the drawer */
  open() {
    if (this.isOpen) return;

    // Captured before focus moves into the panel, so close() can hand it back
    this.#returnFocusTo =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    this.#backdrop?.classList.add("open");
    this.setAttribute("open", "");
    this.setAttribute("role", "dialog");
    this.setAttribute("aria-modal", "true");
    // A dialog needs an accessible name (the modal element does the same):
    // bind to the first heading in the content, unless the markup already
    // carries a label. Screen readers otherwise announce a bare "dialog".
    if (!this.hasAttribute("aria-label") && !this.hasAttribute("aria-labelledby")) {
      const heading = this.querySelector("drawer-content")?.querySelector("h1, h2, h3, h4, h5, h6");
      if (heading) {
        if (!heading.id) heading.id = `drawer-heading-${this.id || Math.random().toString(36).slice(2)}`;
        this.setAttribute("aria-labelledby", heading.id);
      }
    }
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", this.#boundKeydown);

    // After the frame that made the panel visible — a hidden element cannot take focus
    requestAnimationFrame(() => this.#focusFirst());

    this.dispatchEvent(new CustomEvent("drawer:open", { bubbles: true }));
  }

  /** Closes the drawer */
  close() {
    this.#backdrop?.classList.remove("open");
    this.removeAttribute("open");
    // A closed drawer must stop advertising itself as an open modal dialog
    this.removeAttribute("role");
    this.removeAttribute("aria-modal");
    document.removeEventListener("keydown", this.#boundKeydown);

    // Only restore scroll if no other drawers/modals are open
    const openElements = document.querySelectorAll(
      "drawer-element[open], modal-element[open]",
    );
    if (openElements.length === 0) {
      document.body.style.overflow = "";
    }

    // Hand focus back to whatever opened the drawer, rather than dropping it on <body>.
    // The trigger may be gone by now (a re-rendered section), hence the connected check.
    const returnTo = this.#returnFocusTo;
    this.#returnFocusTo = null;
    if (returnTo?.isConnected) returnTo.focus();

    this.dispatchEvent(new CustomEvent("drawer:close", { bubbles: true }));
  }

  /** @returns {boolean} Whether the drawer is open */
  get isOpen() {
    return this.#backdrop?.classList.contains("open") ?? false;
  }

  // ── Focus management ──────────────────────────────────────
  // Content is slotted, so the focusable nodes live in the light DOM under
  // <drawer-content> — not in the shadow root that renders the panel.

  /** @returns {HTMLElement[]} Tabbable descendants of the panel, in document order */
  #focusable() {
    const content = this.querySelector("drawer-content");
    if (!content) return [];

    // getClientRects() rather than offsetParent: the panel renders inside the shadow root,
    // and offsetParent is specified not to reach across that boundary.
    return [...content.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
      (el) => !el.disabled && el.getClientRects().length > 0,
    );
  }

  /** Moves focus into the panel; falls back to the panel itself when it holds no controls */
  #focusFirst() {
    if (!this.isOpen) return;

    const [first] = this.#focusable();
    if (first) {
      first.focus();
      return;
    }

    const content = this.querySelector("drawer-content");
    if (!content) return;
    // -1 keeps it out of the Tab cycle but still lets it receive focus programmatically
    content.setAttribute("tabindex", "-1");
    content.focus();
  }

  /**
   * Keeps Tab inside the panel. Without this, tabbing past the last control walks into the
   * page behind the backdrop, which is unreachable by mouse and hidden from view.
   * @param {KeyboardEvent} e
   */
  #trapTab(e) {
    const focusable = this.#focusable();
    if (focusable.length === 0) {
      // Nothing to cycle between — pin focus to the panel rather than letting it escape
      e.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    // Focus outside the panel (initial open, or a script moved it) — pull it back in
    if (!this.contains(active)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
      return;
    }

    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

class DrawerToggle extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", () => {
      const drawerId = this.getAttribute("drawer-id");
      if (!drawerId) return;

      const drawer = document.getElementById(drawerId);
      if (drawer?.isOpen) {
        window.dispatchEvent(new Event(`close-drawer-${drawerId}`));
      } else {
        window.dispatchEvent(new Event(`open-drawer-${drawerId}`));
      }
    });
  }
}

class DrawerContent extends HTMLElement {
  connectedCallback() {
    this.setAttribute("slot", "content");
  }
}

// Guarded: this file is loaded from more than one section (and in mixed
// classic/module script modes), and a second evaluation must not throw
// NotSupportedError on an already-registered name.
if (!customElements.get("drawer-element")) customElements.define("drawer-element", DrawerElement);
if (!customElements.get("drawer-toggle")) customElements.define("drawer-toggle", DrawerToggle);
if (!customElements.get("drawer-content")) customElements.define("drawer-content", DrawerContent);
